/**
 * React hooks for offline functionality
 */

import { useState, useEffect, useCallback } from 'react';
import { getOnlineStatus, onConnectivityChange, getPendingSyncCount, syncQueuedOperations } from '../services/offlineApi';

/**
 * Hook to track online/offline status
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(getOnlineStatus());

  useEffect(() => {
    const unsubscribe = onConnectivityChange(setIsOnline);
    return unsubscribe;
  }, []);

  return isOnline;
}

/**
 * Hook to track pending sync items
 */
export function usePendingSync() {
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const isOnline = useOnlineStatus();

  const refreshCount = useCallback(async () => {
    const count = await getPendingSyncCount();
    setPendingCount(count);
  }, []);

  const triggerSync = useCallback(async () => {
    if (!isOnline || isSyncing) return;

    setIsSyncing(true);
    try {
      await syncQueuedOperations();
      await refreshCount();
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, refreshCount]);

  useEffect(() => {
    refreshCount();

    // Listen for sync completions
    const handleSyncComplete = () => refreshCount();
    window.addEventListener('sync-complete', handleSyncComplete);

    return () => window.removeEventListener('sync-complete', handleSyncComplete);
  }, [refreshCount]);

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && pendingCount > 0) {
      triggerSync();
    }
  }, [isOnline, pendingCount, triggerSync]);

  return { pendingCount, isSyncing, triggerSync, refreshCount };
}

/**
 * Hook for PWA update management
 */
export function usePWAUpdate() {
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    // Check if service worker is available
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg);

        // Check for updates periodically
        const checkForUpdates = () => {
          reg.update().catch(console.error);
        };

        // Check every 5 minutes
        const interval = setInterval(checkForUpdates, 5 * 60 * 1000);

        return () => clearInterval(interval);
      });

      // Listen for new service worker
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    }
  }, []);

  useEffect(() => {
    if (!registration) return;

    const handleUpdate = () => {
      if (registration.waiting) {
        setNeedsUpdate(true);
      }
    };

    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      newWorker?.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          setNeedsUpdate(true);
        }
      });
    });

    // Check if update is already waiting
    if (registration.waiting) {
      setNeedsUpdate(true);
    }
  }, [registration]);

  const applyUpdate = useCallback(() => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }, [registration]);

  const dismissUpdate = useCallback(() => {
    setNeedsUpdate(false);
  }, []);

  return { needsUpdate, applyUpdate, dismissUpdate };
}

/**
 * Hook to detect if app was installed as PWA
 */
export function useIsPWA() {
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    // Check if running as standalone PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsPWA(isStandalone);
  }, []);

  return isPWA;
}

/**
 * Hook for install prompt (Add to Home Screen)
 */
export function useInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const isPWA = useIsPWA();

  useEffect(() => {
    if (isPWA) return; // Already installed

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isPWA]);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) return false;

    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;

    setInstallPrompt(null);
    setIsInstallable(false);

    return outcome === 'accepted';
  }, [installPrompt]);

  return { isInstallable, promptInstall };
}
